import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY } from '@/shared/hooks/api/lvdComplianceFiles'

export const LVD_PRODUCT_COMPLIANCE_FILE_SLOT_QUERY_KEY =
  'lvdProductComplianceFileSlot' as const

export type LvdProductComplianceFileEvidenceSlot =
  | 'DRAWINGS_SCHEMAS'
  | 'STANDARDS_TEST_REPORT'
  | 'EXAMINATIONS_OR_CALCULATIONS'
  | 'INSTRUCTIONS_AND_SAFETY_INFORMATION'

export type LvdComplianceFileAttachment = {
  id: string
  fileName: string
  contentType: string | null
  fileSizeKb: number | null
  uploadedAt: string
  uploadedByUserId: string | null
}

export type LvdComplianceFileSlotAttachmentsView = {
  files: LvdComplianceFileAttachment[]
}

function slotQueryKey(productId: string, slotKind: LvdProductComplianceFileEvidenceSlot) {
  return [LVD_PRODUCT_COMPLIANCE_FILE_SLOT_QUERY_KEY, productId, slotKind] as const
}

export function useGetLvdComplianceFileSlot(
  productId: string | undefined,
  slotKind: LvdProductComplianceFileEvidenceSlot,
  options?: { enabled?: boolean }
) {
  return useQuery<LvdComplianceFileSlotAttachmentsView, Error>({
    queryKey: slotQueryKey(productId ?? '', slotKind),
    queryFn: async () => {
      const response = await apiClient.get<LvdComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/lvd/compliance-file/slots/${slotKind}`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      productId != null &&
      productId.length > 0,
  })
}

export function useUploadLvdComplianceFileSlot(
  productId: string,
  slotKind: LvdProductComplianceFileEvidenceSlot
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('fileName', file.name)
      const response = await apiClient.post<LvdComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/lvd/compliance-file/slots/${slotKind}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(slotQueryKey(productId, slotKind), data)
      void queryClient.invalidateQueries({
        queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useDeleteLvdComplianceFileSlotAttachment(
  productId: string,
  slotKind: LvdProductComplianceFileEvidenceSlot
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await apiClient.delete<LvdComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/lvd/compliance-file/slots/${slotKind}/${attachmentId}`
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(slotQueryKey(productId, slotKind), data)
      void queryClient.invalidateQueries({
        queryKey: [LVD_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function isLvdComplianceFileSlotFilled(
  slot: LvdComplianceFileSlotAttachmentsView | undefined
): boolean {
  return (slot?.files.length ?? 0) > 0
}
